import { TranslateService } from '@ngx-translate/core';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PhonePrefix, PhonePrefixService } from 'src/app/pages/new-service/services/phone-prefix.service';
import { v4 as uuidv4 } from 'uuid';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { LocalStorageKeys } from 'src/app/core/enums/LocalStorageKeys';
import { LANGUAGE_TO_COUNTRY_CODE } from '../constants/language-country.constants';



@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss']
})
export class PhoneNumberInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() phoneNumber: string | null | undefined = '';
  @Input() defaultPrefix: PhonePrefix | null = { code: "ES", prefix: "+34", url: "/assets/images/flags/spain.webp", nameTranslated: this.translateService.instant('FLAG_COUNTRY.ES') };
  @Input() showValidationErrors: boolean = false;

  @Output() phoneChange = new EventEmitter<string>();

  prefixes: PhonePrefix[] = [];
  FavoritePrefixes: PhonePrefix[] = [];
  uniqueId: string = uuidv4();

  selectedPrefix: PhonePrefix | undefined;
  localNumber = new FormControl('');
  dropdownOpen: boolean = false;

  validationMessage: string | null = null;

  private readonly destroy$ = new Subject<void>();


  constructor(private readonly phonePrefixService: PhonePrefixService, private readonly translateService: TranslateService, private readonly localStorageService: LocalStorageService) {
    this.localNumber.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(raw => this.handleLocalValueChange(raw));
  }

  ngOnInit() {
    this.setData();
  }

  async setData() {
    this.prefixes = await this.phonePrefixService.getPhonePrefixesSorted();
    this.FavoritePrefixes = this.prefixes.filter(p => p.code === 'ES' || p.code === 'FR' || p.code === 'GB');
    this.setDefaultPrefixBasedOnLanguage();
    this.initializePhoneData();
  }

  private setDefaultPrefixBasedOnLanguage() {
    const language = this.localStorageService.get<string>(LocalStorageKeys.Language) || 'en';
    const countryCode = LANGUAGE_TO_COUNTRY_CODE[language] || 'GB';
    const prefix = this.prefixes.find(p => p.code === countryCode);
    if (prefix) {
      this.defaultPrefix = prefix;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.phoneNumber && changes['phoneNumber'].previousValue !== this.phoneNumber) {
      this.initializePhoneData();
    }
    if (changes['showValidationErrors'] && changes['showValidationErrors'].currentValue) {
      this.validateLocalNumber(this.localNumber.value ?? '');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private sanitizeDigits(input: string): string {
    return (input || '').replace(/[^\d]/g, '');
  }

  private handleLocalValueChange(rawValue: any) {
    const raw = (rawValue ?? '').toString();

    const cleaned = this.sanitizeDigits(raw);

    const maxLocal = this.getMaxLocalLengthForSelectedPrefix();
    const limited = cleaned.slice(0, maxLocal);

    if (limited !== raw) {
      this.localNumber.setValue(limited, { emitEvent: false });
    }

    this.validateLocalNumber(limited);
    this.emitPhoneNumber();
  }

  initializePhoneData() {
    if (!this.prefixes?.length) return;
    const sanitizedPhone = (this.phoneNumber ?? '').replace(/\s+/g, '');

    const matched = this.prefixes.find(p => sanitizedPhone.startsWith(p.prefix));
    if (matched) {
      this.selectedPrefix = matched;
      this.localNumber.setValue(sanitizedPhone.replace(matched.prefix, ''));

      this.selectedPrefix = matched;
      const local = sanitizedPhone.replace(matched.prefix, '');
      const cleaned = this.sanitizeDigits(local);
      const maxLocal = this.getMaxLocalLengthForSelectedPrefix();
      this.localNumber.setValue(cleaned.slice(0, maxLocal), { emitEvent: false });

    } else {
      this.selectedPrefix = this.defaultPrefix || this.prefixes[0];

      const after = sanitizedPhone.replace(/^\+?\d{1,3}/, '');
      const cleaned = this.sanitizeDigits(after);
      const maxLocal = this.getMaxLocalLengthForSelectedPrefix();
      this.localNumber.setValue(cleaned.slice(0, maxLocal), { emitEvent: false });
    }
    this.emitPhoneNumber();
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  public getMaxLocalLengthForSelectedPrefix(): number {
    if (!this.selectedPrefix) return 12;
    switch (this.selectedPrefix.code) {
      case 'ES': return 9;
      case 'FR': return 9;
      case 'GB': return 10;
      case 'IT': return 9;
      case 'DE': return 11;
      case 'PT': return 9;
      default: return 12;
    }
  }

  formatLocalForDisplay(localVal: string | null | undefined): string {
    const digits = this.sanitizeDigits(localVal ?? '');
    return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  }

  trackByCode(_index: number, item: PhonePrefix) {
    return item.code;
  }

  onPrefixChange(p: PhonePrefix | undefined) {
    this.selectedPrefix = p;
    this.dropdownOpen = false;

    const current = String(this.localNumber.value ?? '');
    const limited = current.slice(0, this.getMaxLocalLengthForSelectedPrefix());

    if (current !== limited) {
      this.localNumber.setValue(limited);
    } else {
      this.validateLocalNumber(current);
      this.emitPhoneNumber();
    }
    this.emitPhoneNumber();
  }

  private validateLocalNumber(local: string) {
    const control = this.localNumber;
    const cleaned = this.sanitizeDigits(local ?? '');
    const country = this.selectedPrefix?.code ?? undefined;

    const max = this.getMaxLocalLengthForSelectedPrefix();
    const min = Math.max(6, Math.floor(max * 0.7));

    if (cleaned.length === 0) {
      this.validationMessage = this.translateService.instant('phone_number_input.validation_required') || 'El número es obligatorio.';
      control.setErrors({ required: true });
      return;
    }

    if (cleaned.length < min) {
      this.validationMessage = this.translateService.instant('phone_number_input.validation_too_short', { min });
      control.setErrors({ minlength: true });
      return;
    }
    if (cleaned.length > max) {
      this.validationMessage = this.translateService.instant('phone_number_input.validation_too_long', { max });
      control.setErrors({ maxlength: true });
      return;
    }

    try {
      const candidateE164 = `${this.selectedPrefix?.prefix || ''}${cleaned}`;
      let parsed = parsePhoneNumberFromString(candidateE164, country as any);

      if (!parsed) {
        parsed = parsePhoneNumberFromString(cleaned, country as any);
      }

      if (parsed && parsed.isValid()) {
        this.validationMessage = null;
        control.setErrors(null);
        return;
      }

      if (parsed && !parsed.isValid()) {
        this.validationMessage = this.translateService.instant('phone_number_input.validation_invalid') || 'El número no es válido para el país seleccionado.';
        control.setErrors({ invalidPhone: true });
        return;
      }

      this.validationMessage = this.translateService.instant('phone_number_input.validation_invalid') || 'El número no es válido para el país seleccionado.';
      control.setErrors({ invalidPhone: true });
    } catch (e) {
      this.validationMessage = this.translateService.instant('phone_number_input.validation_invalid') || 'El número no es válido para el país seleccionado.';
      control.setErrors({ invalidPhone: true });
    }
  }

  private emitPhoneNumber() {
    const local = (this.localNumber.value ?? '').toString().replace(/\s+/g, '');
    if (!local) {
      this.phoneChange.emit('');
      return;
    }
    const fullPhone = `${this.selectedPrefix?.prefix || ''}${local}`;
    this.phoneChange.emit(fullPhone);

  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const targetElement = event.target as HTMLElement;
    const element = document.getElementById(`${this.uniqueId}`);
    if (element && !element.contains(targetElement)) {
      this.dropdownOpen = false;
    }
  }
}
